import React from 'react';
import { Alert, Checkbox, Divider, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { useSWRConfig } from 'swr';
import moment from 'moment';
import {
  AccountData,
  AdminPassCreateDTO,
  PassOrderPageData,
  PassScheduleSelectionDTO,
  PassScheduleSelectionType,
  PassService,
  PassType,
} from '../../api';
import { ApiError } from '../../api/core/ApiError';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPages, getPassOrderPages } from '../../utils/AccountUtils';
import { useLanguage } from '../../common/GeneralUtils';
import { useSafeExecute } from '../../common/apiNotifications/safeExecute';
import { useOrderPageLocationGroups } from '../../utils/useOrderPageLocationGroups';
import TitledDrawer from '../../common/ui/TitledDrawer';
import { FormDatePicker } from '../../experienceSettings/formComponents/FormDatePicker';
import { DATE_FORMAT } from '../../calendar/calendarWrapper/CalendarWrapper';
import { isRecurringPassType, supportsSchedule } from './utils';
import { FixedSchedulePicker } from './components/schedulePanel/FixedSchedulePicker';

type CreateAdminPassDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const PASS_TYPE_SORT_ORDER: Record<PassType, number> = {
  [PassType.PUNCH_BASED]: 0,
  [PassType.UNLIMITED_MONTHLY]: 1,
  [PassType.FIXED_SCHEDULE_MONTHLY]: 2,
};

const PASS_TYPE_LABEL_KEY: Record<PassType, string> = {
  [PassType.PUNCH_BASED]: 'passes.pass_type.punch_based',
  [PassType.UNLIMITED_MONTHLY]: 'passes.pass_type.unlimited_monthly',
  [PassType.FIXED_SCHEDULE_MONTHLY]: 'passes.pass_type.fixed_schedule_monthly',
};

const passTypeSortValue = (passType: PassType | undefined): number => (passType ? PASS_TYPE_SORT_ORDER[passType] : 99);

const sortByPassType = (pages: PassOrderPageData[]): PassOrderPageData[] =>
  [...pages].sort((a, b) => passTypeSortValue(a.pass_type) - passTypeSortValue(b.pass_type));

type CreateAdminPassFormValues = {
  order_page_ids: string[];
  schedule_selections_by_page: Record<string, PassScheduleSelectionDTO[]>;
  permanent_schedule_hold_by_page: Record<string, boolean>;
  firstname: string;
  lastname: string;
  alternative_full_name: string;
  email: string;
  phone: string;
  comment: string;
  skip_admin_confirmation_email: boolean;
  start_date: string;
};

const mapAdminPassCreateError = (error: ApiError, t: (key: string) => string): string => {
  const detail = error.body?.detail as string | undefined;
  if (detail === 'scheduleSelectionsRequired') return t('passes.fixed_schedule_errors.schedule_selections_required');
  if (detail === 'fixedScheduleMonthlyMissingWeeklySessionCount')
    return t('passes.fixed_schedule_errors.template_missing_weekly_count');
  if (detail === 'scheduledWeeklyRequiresEventTarget') return t('passes.fixed_schedule_errors.event_target_required');
  return error.body?.message || t('api.error.default');
};

const CreateAdminPassDialog = ({ open, onClose }: CreateAdminPassDrawerProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { mutate } = useSWRConfig();
  const safeExecute = useSafeExecute();

  const passOrderPages = getPassOrderPages(account);
  const orderPages = getOrderPages(account);
  const { activeLocations, groupByLocation, locationLabel } = useOrderPageLocationGroups(account);

  const derivePassLocationId = (page: PassOrderPageData): string | undefined => {
    const firstRedeemablePageId = page.redeemable_on?.[0]?.order_page_id;
    if (!firstRedeemablePageId) return undefined;
    return orderPages.find((o) => o.id === firstRedeemablePageId)?.location_id;
  };

  const passLocationGroups = React.useMemo(() => {
    if (!groupByLocation) return [{ location: null as AccountData['locations'][number] | null, pages: passOrderPages }];
    const knownIds = new Set(activeLocations.map((l) => l.id));
    const other = passOrderPages.filter((p) => {
      const lid = derivePassLocationId(p);
      return !lid || !knownIds.has(lid);
    });
    const groups: { location: AccountData['locations'][number] | null; pages: PassOrderPageData[] }[] = [];
    if (other.length > 0) groups.push({ location: null, pages: other });
    activeLocations.forEach((loc) => {
      const locPages = passOrderPages.filter((p) => derivePassLocationId(p) === loc.id);
      if (locPages.length > 0) groups.push({ location: loc, pages: locPages });
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passOrderPages, orderPages, activeLocations, groupByLocation]);

  const validationSchema = yup.object().shape({
    order_page_ids: yup.array().of(yup.string()).min(1, t('editOrderPage.yup.required')),
    firstname: yup.string().required(t('editOrderPage.yup.required')),
    lastname: yup.string().required(t('editOrderPage.yup.required')),
    email: yup.string().email().required(t('editOrderPage.yup.required')),
    phone: yup.string().required(t('editOrderPage.yup.required')),
  });

  const initialValues: CreateAdminPassFormValues = {
    order_page_ids: [],
    schedule_selections_by_page: {},
    permanent_schedule_hold_by_page: {},
    firstname: '',
    lastname: '',
    alternative_full_name: '',
    email: '',
    phone: '',
    comment: '',
    skip_admin_confirmation_email: false,
    start_date: moment().startOf('day').format(DATE_FORMAT),
  };

  const validateSchedules = (values: CreateAdminPassFormValues): string | null => {
    for (const orderPageId of values.order_page_ids) {
      const page = passOrderPages.find((p) => p.id === orderPageId);
      const got = values.schedule_selections_by_page[orderPageId]?.length ?? 0;
      if (page?.pass_type === PassType.FIXED_SCHEDULE_MONTHLY) {
        const required = page.weekly_session_count ?? 0;
        if (got !== required) {
          return t('passes.fixed_schedule_errors.schedule_count_mismatch', { expected: required, got });
        }
      } else if (page?.pass_type === PassType.PUNCH_BASED) {
        const holdOn = values.permanent_schedule_hold_by_page[orderPageId] ?? false;
        const mustHaveSchedule = page.schedule_selection_type === PassScheduleSelectionType.WEEKLY_SLOTS || holdOn;
        if (mustHaveSchedule && got < 1) {
          return t('passes.fixed_schedule_errors.schedule_selections_required');
        }
      }
    }
    return null;
  };

  const handleSubmit = async (values: CreateAdminPassFormValues, { setStatus }: { setStatus: (s: any) => void }) => {
    const scheduleError = validateSchedules(values);
    if (scheduleError) {
      setStatus({ error: scheduleError });
      return;
    }
    setStatus(undefined);
    try {
      await safeExecute({
        successMessage: t('passes.admin_pass_created'),
        onErrorMessageGenerator: (error) => mapAdminPassCreateError(error, t),
        apiCall: () =>
          Promise.all(
            values.order_page_ids.map((orderPageId) => {
              const selectedPassOrderPage = passOrderPages.find((p) => p.id === orderPageId);
              const passType = selectedPassOrderPage?.pass_type;
              const canCarrySchedule = supportsSchedule(passType);
              const selections = values.schedule_selections_by_page[orderPageId];
              const isPunchBased = passType === PassType.PUNCH_BASED;
              const dto: AdminPassCreateDTO = {
                language,
                account_id: account.id,
                order_page_id: orderPageId,
                price: selectedPassOrderPage?.price || 0,
                firstname: values.firstname,
                lastname: values.lastname,
                alternative_full_name: values.alternative_full_name || undefined,
                email: values.email,
                phone: values.phone,
                comment: values.comment || undefined,
                skip_admin_confirmation_email: values.skip_admin_confirmation_email,
                start_date: values.start_date || undefined,
                schedule_selections: canCarrySchedule && selections?.length ? selections : undefined,
                permanent_schedule_hold: isPunchBased
                  ? values.permanent_schedule_hold_by_page[orderPageId] ?? false
                  : undefined,
              };
              return PassService.createAdminPass(dto);
            }),
          ),
      });
      await mutate((key: any) => key?.opId === 'getPasses');
      onClose();
    } catch {
      // toast already shown by safeExecute; keep dialog open so user can retry
    }
  };

  return (
    <TitledDrawer title={t('passes.create_admin_pass')} open={open} onClose={onClose}>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, setFieldValue, isSubmitting, errors, touched, submitForm, status }) => (
          <Form style={{ height: '100%' }}>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              justifyContent="space-between"
              width="100%"
              overflow="hidden"
              height="100%">
              <Grid
                gap={1.5}
                container
                flexDirection="column"
                flexWrap="nowrap"
                flex={1}
                minHeight={0}
                sx={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pt: { xs: '12px', md: '18px' },
                  pb: 2,
                  paddingInline: { xs: '12px', md: '24px' },
                }}>
                {/* Pass Product Selector */}
                <Typography variant="subtitle2" fontWeight={700}>
                  {t('passes.pass_product')}
                </Typography>
                <Grid container flexDirection="column">
                  {passLocationGroups.map((group, idx) => (
                    <Grid
                      key={group.location?.id || 'other'}
                      container
                      flexDirection="column"
                      mt={groupByLocation && idx > 0 ? 1 : 0}>
                      {groupByLocation && (
                        <Typography variant="caption" color="text.secondary">
                          {locationLabel(group.location)}
                        </Typography>
                      )}
                      {sortByPassType(group.pages).map((page: PassOrderPageData) => (
                        <FormControlLabel
                          key={page.id}
                          control={
                            <Checkbox
                              checked={values.order_page_ids.includes(page.id)}
                              onChange={() => {
                                const ids = values.order_page_ids.includes(page.id)
                                  ? values.order_page_ids.filter((id) => id !== page.id)
                                  : [...values.order_page_ids, page.id];
                                void setFieldValue('order_page_ids', ids);
                              }}
                            />
                          }
                          label={
                            <Grid container gap={1} alignContent="baseline">
                              <Typography sx={{ wordBreak: 'break-word' }}>
                                {page.label[language] || page.id}
                              </Typography>
                              {page.pass_type && (
                                <Typography variant="caption" color="text.secondary">
                                  [{t(PASS_TYPE_LABEL_KEY[page.pass_type])}]
                                </Typography>
                              )}
                            </Grid>
                          }
                        />
                      ))}
                    </Grid>
                  ))}
                  {touched.order_page_ids && errors.order_page_ids && (
                    <Typography color="error" variant="body2">
                      {errors.order_page_ids}
                    </Typography>
                  )}
                </Grid>

                {values.order_page_ids.some((id) =>
                  isRecurringPassType(passOrderPages.find((p) => p.id === id)?.pass_type),
                ) && (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    {t('passes.recurring.adminCreateWarning')}
                  </Alert>
                )}

                {values.order_page_ids
                  .map((id) => passOrderPages.find((p) => p.id === id))
                  .filter((p): p is PassOrderPageData => supportsSchedule(p?.pass_type))
                  .map((page) => {
                    const isPunchBased = page.pass_type === PassType.PUNCH_BASED;
                    const pickerProps = isPunchBased
                      ? {
                          minSelections:
                            page.schedule_selection_type === PassScheduleSelectionType.WEEKLY_SLOTS ? 1 : 0,
                          maxSelections: Infinity,
                        }
                      : {};
                    const holdChecked = values.permanent_schedule_hold_by_page[page.id] ?? false;
                    return (
                      <Grid key={page.id} container flexDirection="column" gap={1}>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight={700}>
                          {page.label[language] || page.id}
                        </Typography>
                        <FixedSchedulePicker
                          passOrderPage={page}
                          accountId={account.id}
                          value={values.schedule_selections_by_page[page.id] || []}
                          onChange={(sel) =>
                            setFieldValue('schedule_selections_by_page', {
                              ...values.schedule_selections_by_page,
                              [page.id]: sel,
                            })
                          }
                          {...pickerProps}
                        />
                        {isPunchBased && (
                          <Grid container flexDirection="column">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={holdChecked}
                                  onChange={(e) =>
                                    setFieldValue('permanent_schedule_hold_by_page', {
                                      ...values.permanent_schedule_hold_by_page,
                                      [page.id]: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label={t('passes.permanent_schedule_hold_toggle')}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {t('passes.permanent_schedule_hold_helper')}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    );
                  })}

                <FormDatePicker
                  fieldName="start_date"
                  label={t('passes.start_date')}
                  isFieldUpdate={false}
                  fullWidth
                  disablePast={false}
                />

                <TextField
                  label={t('passes.firstname')}
                  value={values.firstname}
                  onChange={(e) => setFieldValue('firstname', e.target.value)}
                  error={touched.firstname && !!errors.firstname}
                  helperText={touched.firstname && errors.firstname}
                  fullWidth
                />

                <TextField
                  label={t('passes.lastname')}
                  value={values.lastname}
                  onChange={(e) => setFieldValue('lastname', e.target.value)}
                  error={touched.lastname && !!errors.lastname}
                  helperText={touched.lastname && errors.lastname}
                  fullWidth
                />

                <TextField
                  label={t('order_page.alternative_full_name')}
                  value={values.alternative_full_name}
                  onChange={(e) => setFieldValue('alternative_full_name', e.target.value)}
                  fullWidth
                />

                <TextField
                  label={t('passes.email_field')}
                  value={values.email}
                  onChange={(e) => setFieldValue('email', e.target.value)}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  fullWidth
                />

                <TextField
                  label={t('passes.phone')}
                  value={values.phone}
                  onChange={(e) => setFieldValue('phone', e.target.value)}
                  error={touched.phone && !!errors.phone}
                  helperText={touched.phone && errors.phone}
                  fullWidth
                />

                <TextField
                  label={t('passes.comment')}
                  value={values.comment}
                  onChange={(e) => setFieldValue('comment', e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.skip_admin_confirmation_email}
                      onChange={(e) => setFieldValue('skip_admin_confirmation_email', e.target.checked)}
                    />
                  }
                  label={t('passes.skip_confirmation_email')}
                />
              </Grid>

              <Grid
                container
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
                sx={{
                  flexShrink: 0,
                  py: 1.5,
                  paddingInline: { xs: '12px', md: '24px' },
                  borderTop: '1px solid',
                  borderColor: 'mb.lineSoft',
                }}>
                {status?.error && (
                  <Typography variant="body2" color="error" sx={{ textAlign: 'end', flex: 1, minWidth: 0 }}>
                    {status.error}
                  </Typography>
                )}
                <LoadingButton
                  loading={isSubmitting}
                  variant="contained"
                  sx={{ textTransform: 'none' }}
                  onClick={submitForm}>
                  {t('common.create')}
                </LoadingButton>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </TitledDrawer>
  );
};

export default CreateAdminPassDialog;
